import { styled } from '@mui/joy';
// REMINDER: mui/joy does not explicitly export FormControlContext but we need it for internal integrations.
import JoyFormControlContext from '@mui/joy/FormControl/FormControlContext';
import JoyLabel, {
  FormLabelProps as JoyFormLabelProps,
  formLabelClasses,
} from '@mui/joy/FormLabel';
import JoyTypography, {
  TypographyProps as JoyTypographyProps,
} from '@mui/joy/Typography';
import { mergeProps } from '@react-aria/utils';
import clsx from 'clsx';
import { ComponentPropsWithoutRef, forwardRef, useContext } from 'react';

export const Fieldset = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function Fieldset({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      {...mergeProps(props, {
        className: clsx([
          className,
          '[&>*+[data-slot=control]]:mt-5 [&>[data-slot=text]]:mt-2',
        ]),
      })}
    />
  );
});

const StyledLegend = styled(JoyTypography)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(3),
}));

export const Legend = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof StyledLegend>
>(function FieldGroup(props, ref) {
  return (
    <StyledLegend
      {...props}
      level='title-md'
      sx={{ ...props.sx, fontWeight: 600 }}
      ref={ref}
      data-slot='legend'
    />
  );
});

type FieldGroupLayoutTypes = 'stacked' | 'grid';
const fieldGroupLayoutStyles: Record<FieldGroupLayoutTypes, string[]> = {
  grid: ['grid md:grid-cols-2 gap-6'],
  stacked: ['flex flex-col gap-6'],
};
export const FieldGroup = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'> & { layout?: 'stacked' | 'grid' }
>(function FieldGroup({ layout = 'stacked', ...props }, ref) {
  const layoutStyles = fieldGroupLayoutStyles[layout];
  return (
    <div
      data-slot='control'
      {...props}
      className={clsx([props.className, layoutStyles])}
      ref={ref}
    />
  );
});

export const Label = forwardRef<HTMLLabelElement, JoyFormLabelProps>(
  function Label({ required, children, ...props }, ref) {
    return (
      <JoyLabel
        ref={ref}
        data-slot='label'
        required={false}
        {...props}
        sx={{
          margin: '0px',
          ...props.sx,
        }}
      >
        <span className='flex gap-1 text-base m-0'>
          {required && (
            <span
              className={clsx('text-red-500', formLabelClasses.asterisk)}
              aria-hidden={true}
            >
              *
            </span>
          )}
          {children}
        </span>
      </JoyLabel>
    );
  },
);

export const Description = forwardRef<HTMLElement, JoyTypographyProps>(
  function Description(props, ref) {
    const formControl = useContext(JoyFormControlContext);
    const ariaDescribedBy = formControl ? formControl['aria-describedby'] : '';

    return (
      <JoyTypography
        level='body-sm'
        {...props}
        sx={{
          fontFamily: 'var(--joy-fontFamily-body)',
          fontSize: 'var(--FormHelperText-fontSize, var(--joy-fontSize-sm))',
          lineHeight:
            'var(--FormHelperText-lineHeight, var(--joy-lineHeight-sm))',
          color:
            'var(--FormHelperText-color, var(--joy-palette-text-tertiary))',
          margin: '0px',
          ...props.sx,
        }}
        data-slot='description'
        ref={ref}
        aria-describedby={
          props['aria-describedby']
            ? props['aria-describedby']
            : ariaDescribedBy
        }
      />
    );
  },
);

export function FieldWrapper({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  return (
    <div
      className={clsx([
        '[&>[data-slot=label]+[data-slot=control]]:mt-2',
        '[&>[data-slot=label]+[data-slot=description]]:mt-1',
        '[&>[data-slot=description]+[data-slot=control]]:mt-2',
        '[&>[data-slot=control]+[data-slot=description]]:mt-2',
        '[&>[data-slot=control]+[data-slot=error]]:mt-2',
        '[&>[data-slot=label]]:font-medium',
      ])}
    >
      {children}
    </div>
  );
}
